import { CartItemsComponent } from '@theme/component-cart-items';
import { StandardEvents } from '@shopify/events';

const fields = ['bec_digital_consent', 'bec_digital_consent_version', 'bec_digital_consent_locale', 'bec_digital_consent_text', 'bec_digital_consent_at', 'bec_digital_consent_items'];
const fingerprint = (items) => JSON.stringify(items.map((item) => [item.key, item.quantity]).sort((a, b) => a[0].localeCompare(b[0])));
let attributeQueue = Promise.resolve();

function updateAttributes(url, attributes) {
  const task = attributeQueue.catch(() => {}).then(async () => {
    const response = await fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin', body: JSON.stringify({ attributes })
    });
    if (!response.ok) throw new Error('Consent save failed');
    return response.json();
  });
  attributeQueue = task;
  return task;
}

class BarbaraDigitalConsent extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.refresh());
    this.observer.observe(this.closest('cart-items-component') || this.parentElement, { childList: true, subtree: true });
    this.refresh();
    document.addEventListener(StandardEvents.cartLinesUpdate, () => this.reset(), options);
    document.addEventListener('quantity-selector:update', () => this.reset(), options);
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.reset(); }, options);
  }

  refresh() {
    const checkbox = this.querySelector('input[type="checkbox"]');
    const fresh = checkbox !== this.checkbox;
    this.checkbox = checkbox;
    this.status = this.querySelector('[role="status"]');
    this.container = this.closest('cart-items-component') || this.closest('.shopify-section');
    this.form = this.container?.querySelector('form.cart-form');
    this.button = this.container?.querySelector('button[name="checkout"]');
    if (!this.checkbox || !this.form || !this.button) return;
    if (fresh) {
      this.checkbox.checked = false;
      // A newly rendered unchecked control must invalidate an older cart record.
      // Clearing cart attributes does not change any historical order evidence.
      this.clearSavedConsent();
    }
    this.checkbox.setCustomValidity(this.checkbox.checked ? '' : this.dataset.required);
    if (!this.busy) this.button.disabled = false;
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  show(message) {
    this.status.textContent = message;
    this.status.hidden = !message;
  }

  clearSavedConsent() {
    this.permitted = false;
    this.querySelectorAll('[data-consent-attribute]').forEach((input) => input.remove());
    updateAttributes(this.dataset.updateUrl, Object.fromEntries(fields.map((key) => [key, '']))).catch(() => {});
  }

  reset() {
    if (!this.checkbox) return;
    this.checkbox.checked = false;
    this.checkbox.setCustomValidity(this.dataset.required);
    this.clearSavedConsent();
  }

  async submit(event) {
    this.refresh();
    if (event.submitter?.name !== 'checkout') return;
    if (this.permitted && this.checkbox.checked) { this.permitted = false; return; }
    event.preventDefault();
    if (this.busy) return;
    if (!this.checkbox.checked) {
      this.show(this.dataset.required);
      this.checkbox.reportValidity();
      return;
    }
    this.busy = true;
    this.button.disabled = true;
    this.checkbox.disabled = true;
    this.show(this.dataset.saving);
    try {
      await Promise.all(CartItemsComponent.pendingQuantityUpdates);
      if (!this.isConnected || !this.checkbox.checked) throw new Error('Cart changed');
      const expectedItems = JSON.parse(this.dataset.items);
      const attributes = {
        bec_digital_consent: 'accepted_v522',
        bec_digital_consent_version: this.dataset.version,
        bec_digital_consent_locale: this.dataset.locale,
        bec_digital_consent_text: this.querySelector('[data-consent-text]').textContent.trim(),
        bec_digital_consent_at: new Date().toISOString(),
        bec_digital_consent_items: JSON.stringify(expectedItems)
      };
      const result = await updateAttributes(this.dataset.updateUrl, attributes);
      if (fingerprint(result.items) !== fingerprint(expectedItems.map(([key, quantity]) => ({ key, quantity }))) ||
          !this.isConnected || !this.checkbox.checked) {
        this.reset();
        throw new Error('Cart changed');
      }
      if (fields.some((key) => result.attributes?.[key] !== attributes[key])) throw new Error('Consent save failed');
      for (const [key, value] of Object.entries(attributes)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'attributes[' + key + ']';
        input.value = value;
        input.setAttribute('form', this.form.id);
        input.dataset.consentAttribute = '';
        this.append(input);
      }
      this.checkbox.disabled = false;
      this.button.disabled = false;
      this.permitted = true;
      this.form.requestSubmit(event.submitter);
    } catch (error) {
      this.show(error.message === 'Cart changed' ? this.dataset.changed : this.dataset.failed);
    } finally {
      this.busy = false;
      this.checkbox.disabled = false;
      this.button.disabled = false;
    }
  }
}
if (!customElements.get('barbara-digital-consent')) customElements.define('barbara-digital-consent', BarbaraDigitalConsent);

function consentForForm(form) {
  return [...document.querySelectorAll('barbara-digital-consent')].find((consent) => {
    consent.refresh();
    return consent.form === form;
  });
}
document.addEventListener('click', (event) => {
  const button = event.target.closest?.('button[name="checkout"]');
  if (!button) return;
  const consent = consentForForm(button.form);
  if (consent && !consent.checkbox.checked) {
    event.preventDefault();
    event.stopImmediatePropagation();
    consent.show(consent.dataset.required);
    consent.checkbox.reportValidity();
  }
}, true);
document.addEventListener('submit', (event) => {
  const consent = consentForForm(event.target);
  if (consent) void consent.submit(event);
}, true);
document.addEventListener('change', (event) => {
  const consent = event.target.closest?.('barbara-digital-consent');
  if (!consent || event.target.type !== 'checkbox') return;
  consent.refresh();
  consent.show('');
  if (!consent.checkbox.checked) consent.clearSavedConsent();
}, true);
document.addEventListener('invalid', (event) => {
  const consent = event.target.closest?.('barbara-digital-consent');
  if (consent) { consent.refresh(); consent.show(consent.dataset.required); }
}, true);
